/**
 * Pure helpers behind the day-level "Care" hover card (CareHoverCard).
 * Kept free of React/DOM so placement and totals can be unit tested.
 */

export const CARD_GAP = 4;
export const VIEWPORT_MARGIN = 8;
export const MIN_CARD_HEIGHT = 160;
export const CARD_MAX_WIDTH = 520;

const toNumber = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
};

const roundCents = (value) => Math.round(value * 100) / 100;

/** Rounds a CSS pixel coordinate to a whole *device* pixel (kills sub-pixel jitter at 125%/150% scaling). */
export const snapToDevicePixel = (value, dpr = 1) => {
    const ratio = dpr > 0 ? dpr : 1;
    return Math.round(value * ratio) / ratio;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Decides where the card goes. Deterministic: the same inputs always give the same output, and the
 * caller computes it once when the card opens, so the card can never "flip" while the pointer is on it.
 *
 * anchor / viewport: plain rects ({top,left,right,bottom} / {width,height}); card: natural {width,height}.
 * Returns { top, left, placement, maxHeight } - maxHeight is the height the card is allowed to occupy
 * (its list body scrolls inside it).
 */
export const computePlacement = ({ anchor, card, viewport, gap = CARD_GAP, margin = VIEWPORT_MARGIN, minHeight = MIN_CARD_HEIGHT, dpr = 1 }) => {
    const maxCardWidth = Math.max(0, viewport.width - margin * 2);
    const width = Math.min(card.width, maxCardWidth);
    const naturalHeight = Math.min(card.height, Math.max(0, viewport.height - margin * 2));
    const wanted = Math.min(naturalHeight, Math.max(minHeight, naturalHeight));

    const spaceBelow = viewport.height - anchor.bottom - gap - margin;
    const spaceAbove = anchor.top - gap - margin;

    const centerLeft = (anchor.left + anchor.right) / 2 - width / 2;
    const left = clamp(centerLeft, margin, viewport.width - width - margin);

    let placement;
    let top;
    let maxHeight;

    if (spaceBelow >= wanted) {
        placement = 'bottom';
        top = anchor.bottom + gap;
        maxHeight = wanted;
    } else if (spaceAbove >= wanted) {
        placement = 'top';
        maxHeight = wanted;
        top = anchor.top - gap - maxHeight;
    } else if (Math.max(spaceBelow, spaceAbove) >= minHeight) {
        // Neither side fits the whole list, but one has a usable amount of room: take the bigger
        // side and let the list scroll inside it.
        if (spaceBelow >= spaceAbove) {
            placement = 'bottom';
            top = anchor.bottom + gap;
            maxHeight = spaceBelow;
        } else {
            placement = 'top';
            maxHeight = spaceAbove;
            top = anchor.top - gap - maxHeight;
        }
    } else {
        // Anchor sits mid-viewport with a very tall card: put it beside the anchor instead.
        const height = Math.min(wanted, viewport.height - margin * 2);
        const spaceRight = viewport.width - anchor.right - gap - margin;
        const placeRight = spaceRight >= width || spaceRight >= anchor.left - gap - margin;
        placement = placeRight ? 'right' : 'left';
        maxHeight = height;
        top = clamp(anchor.top, margin, viewport.height - height - margin);
        const sideLeft = placeRight ? anchor.right + gap : anchor.left - gap - width;
        return {
            top: snapToDevicePixel(top, dpr),
            left: snapToDevicePixel(clamp(sideLeft, margin, viewport.width - width - margin), dpr),
            placement,
            maxHeight,
            width
        };
    }

    return {
        top: snapToDevicePixel(clamp(top, margin, viewport.height - margin - 1), dpr),
        left: snapToDevicePixel(left, dpr),
        placement,
        maxHeight,
        width
    };
};

/** Cleans API procedure rows into stable, numeric rows with a React-safe key. */
export const normalizeProcedures = (procedures) =>
    (Array.isArray(procedures) ? procedures : []).map((proc, index) => ({
        key: `${proc?.ProcCode || 'proc'}-${index}`,
        code: String(proc?.ProcCode || '').trim(),
        description: String(proc?.ProcCodeDescription || '').trim(),
        fee: toNumber(proc?.Procfee),
        index
    }));

export const SORT_OPTIONS = {
    original: 'original',
    feeDesc: 'feeDesc',
    code: 'code'
};

export const sortProcedures = (rows, sort) => {
    const list = [...rows];
    if (sort === SORT_OPTIONS.feeDesc) return list.sort((a, b) => Math.abs(b.fee) - Math.abs(a.fee) || a.index - b.index);
    if (sort === SORT_OPTIONS.code) return list.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }) || a.index - b.index);
    return list.sort((a, b) => a.index - b.index);
};

export const filterProcedures = (rows, query) => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => r.code.toLowerCase().includes(q) || r.description.toLowerCase().includes(q));
};

/** Collapses repeated codes into { code, description, count, fee (subtotal) }. */
export const groupProceduresByCode = (rows) => {
    const groups = new Map();
    rows.forEach(r => {
        const existing = groups.get(r.code);
        if (existing) {
            existing.count += 1;
            existing.fee = roundCents(existing.fee + r.fee);
            if (!existing.description && r.description) existing.description = r.description;
        } else {
            groups.set(r.code, { key: `group-${r.code}`, code: r.code, description: r.description, count: 1, fee: roundCents(r.fee), index: r.index });
        }
    });
    return Array.from(groups.values());
};

export const sumFees = (rows) => roundCents(rows.reduce((sum, r) => sum + r.fee, 0));

/**
 * The day's Care figure comes from every record of the day; the list only shows rows that carry a
 * procedure code. When the two differ, return the uncoded remainder so the card can say so
 * instead of silently showing a total that doesn't add up.
 */
export const getUnitemizedAmount = (dayTotal, rows) => {
    const diff = roundCents(toNumber(dayTotal) - sumFees(rows));
    return Math.abs(diff) >= 0.01 ? diff : 0;
};
